import { useState } from 'react'
import { CalendarOff, Check, Plus, Trash2, Wrench } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { resourceProfilesApi, type ResourceProfile } from '@/api/resourceCenterApi'
import { todayIso } from '@/lib/resourceCalendar'
import { AbsenceDialog } from './AbsenceDialog'
import { Empty, ErrorLine, SectionCard, dateRange, useProfileWrite } from './shared'
import { SupportActivityDialog } from './SupportActivityDialog'

/**
 * 4 · Support activities (slide 13): picked from the center list, hours per
 * working day, reserved before PR / RV work. The form lives in a dialog; this
 * card is the tidy list plus a "+ Add support activity" button.
 */
export function SupportSection({ profile, canManage }: { profile: ResourceProfile; canManage: boolean }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const write = useProfileWrite()
  const perDay = profile.supportActivities
    .filter(s => s.fromDate <= todayIso() && (!s.toDate || s.toDate >= todayIso()))
    .reduce((n, s) => n + s.hoursPerDay, 0)

  return (
    <SectionCard
      icon={Wrench}
      title='Support activities'
      hint={perDay > 0 ? `${perDay} h reserved per working day today` : 'Reserved first, before PR / RV'}
      action={
        canManage && (
          <Button size='sm' className='h-8' onClick={() => setDialogOpen(true)}>
            <Plus className='mr-1 h-3.5 w-3.5' /> Add
          </Button>
        )
      }
    >
      {profile.supportActivities.length === 0 ? (
        <Empty>No support activities.</Empty>
      ) : (
        <ul className='space-y-1.5'>
          {profile.supportActivities.map(s => (
            <li key={s.id} className='flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs'>
              <span className='rounded bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-700'>{s.type.code}</span>
              <span className='min-w-0 flex-1 truncate'>
                {s.type.name}
                <span className='block text-muted-foreground'>{dateRange(s.fromDate, s.toDate)}</span>
              </span>
              <span className='font-medium tabular-nums'>{s.hoursPerDay} h/day</span>
              {canManage && (
                <button
                  type='button'
                  aria-label={`Remove ${s.type.name}`}
                  className='text-muted-foreground/60 hover:text-destructive'
                  onClick={() => write.run(() => resourceProfilesApi.removeSupportActivity(profile.userId, s.id))}
                >
                  <Trash2 className='h-3.5 w-3.5' />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ErrorLine message={write.error} />
      {/* Mounted only while open, so each opening starts from a clean form. */}
      {canManage && dialogOpen && <SupportActivityDialog profile={profile} open onOpenChange={setDialogOpen} />}
    </SectionCard>
  )
}

/**
 * 5 · Unavailable events (slide 13): vacation, training, sick, holiday… from
 * the center list, a date range, whole days or a number of hours. Only the
 * person's supervisor can approve one. The form lives in a dialog; this card
 * is the tidy list plus a "+ Add absence" button.
 */
export function AbsenceSection({ profile, canManage }: { profile: ResourceProfile; canManage: boolean }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const write = useProfileWrite()
  const list = [...profile.unavailabilities].sort((a, b) => b.fromDate.localeCompare(a.fromDate))

  return (
    <SectionCard
      icon={CalendarOff}
      title='Absences'
      hint='Taken off the shift before anything else'
      action={
        canManage && (
          <Button size='sm' className='h-8' onClick={() => setDialogOpen(true)}>
            <Plus className='mr-1 h-3.5 w-3.5' /> Add
          </Button>
        )
      }
    >
      {list.length === 0 ? (
        <Empty>No absences planned.</Empty>
      ) : (
        <ul className='space-y-1.5'>
          {list.map(u => (
            <li key={u.id} className='flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs'>
              <span className='rounded bg-rose-50 px-1.5 py-0.5 font-semibold text-rose-700'>{u.type.code}</span>
              <span className='min-w-0 flex-1'>
                <span className='block truncate'>
                  {u.type.name} · {u.hours === null ? 'full days' : `${u.hours} h`}
                </span>
                <span className='block truncate text-muted-foreground'>
                  {dateRange(u.fromDate, u.toDate)}
                  {u.note && ` · ${u.note}`}
                </span>
              </span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-medium',
                  u.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground',
                )}
              >
                {u.status === 'APPROVED' ? 'Approved' : 'Planned'}
              </span>
              {canManage && u.status === 'PLANNED' && (
                <button
                  type='button'
                  title="Approve — only this person's supervisor can"
                  aria-label={`Approve ${u.type.name}`}
                  className='text-muted-foreground hover:text-emerald-700'
                  onClick={() =>
                    write.run(() => resourceProfilesApi.updateUnavailability(profile.userId, u.id, { status: 'APPROVED' }))
                  }
                >
                  <Check className='h-3.5 w-3.5' />
                </button>
              )}
              {canManage && (
                <button
                  type='button'
                  aria-label={`Remove ${u.type.name}`}
                  className='text-muted-foreground/60 hover:text-destructive'
                  onClick={() => write.run(() => resourceProfilesApi.removeUnavailability(profile.userId, u.id))}
                >
                  <Trash2 className='h-3.5 w-3.5' />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ErrorLine message={write.error} />
      {canManage && dialogOpen && <AbsenceDialog profile={profile} open onOpenChange={setDialogOpen} />}
    </SectionCard>
  )
}
